import { api } from '../api/client';
import { showErrorToast } from '../utils/toast';   // Block 233
import { messageFromError } from './apiError';   // Block 233

function normalizeCheckoutPlan(planName) {
  const raw = String(planName || '').trim().toLowerCase();
  if (!raw) return null;
  if (['pro', 'professional'].includes(raw)) return 'pro';
  if (['team', 'team pro', 'teampro'].includes(raw)) return 'team';
  if (['basics', 'basic', 'solo'].includes(raw)) return 'basics';
  if (['personal'].includes(raw)) return 'personal';
  if (['family'].includes(raw)) return 'family';
  if (['enterprise', 'large company', 'large-company'].includes(raw)) return 'enterprise';
  return null;
}

function normalizeBillingPeriod(billingPeriod) {
  const raw = String(billingPeriod || '').trim().toLowerCase();
  if (['annual', 'yearly', 'year'].includes(raw)) return 'annual';
  if (['monthly', 'month'].includes(raw)) return 'monthly';
  return 'monthly';
}

/**
 * Redirect the authenticated user to the Stripe Customer Portal.
 */
export async function redirectToPortal({ fallbackPlanName = 'pro', fallbackBillingPeriod = 'monthly', fallbackQuantity } = {}) {
  try {
    // Block 233: api() THROWS on a non-2xx, so `if (!res.ok)` was unreachable — and
    // with it the fallback that routes a user with no Stripe customer to checkout.
    // A user without a billing account clicking "Manage billing" got
    // "Could not open billing portal. Please try again." every time, forever, instead
    // of being sent to checkout. The branch moves into the catch, where it can run.
    const res = await api('/api/billing/create-portal-session', { method: 'POST' });
    const data = await res.json();
    window.location.href = data.url;
  } catch (err) {
    console.error('Stripe portal error:', err);
    const message = messageFromError(err, 'Failed to create portal session');
    if (err?.status === 400 && /no billing account found/i.test(message)) {
      return redirectToCheckout({
        planName: fallbackPlanName,
        billingPeriod: fallbackBillingPeriod,
        quantity: fallbackQuantity,
      });
    }
    // The message was computed and then discarded before; on a billing path the
    // reason is the whole point.
    showErrorToast(message);
  }
}

/**
 * Redirect the authenticated user to Stripe Checkout.
 * @param {{ planName: string, billingPeriod: string, quantity?: number }} opts
 */
export async function redirectToCheckout({ planName, billingPeriod, quantity }) {
  const normalizedPlanName = normalizeCheckoutPlan(planName);
  const normalizedBillingPeriod = normalizeBillingPeriod(billingPeriod);

  if (!normalizedPlanName || normalizedPlanName === 'enterprise') {
    alert('Please select a valid paid plan to continue.');
    return;
  }

  try {
    const res = await api('/api/billing/create-checkout-session', {
      method: 'POST',
      body: JSON.stringify({
        planName: normalizedPlanName,
        billingPeriod: normalizedBillingPeriod,
        quantity,
      }),
    });
    // Block 233: the !res.ok block was dead here too (api() throws). The message it
    // built was thrown and then discarded by the catch.
    const data = await res.json();
    window.location.href = data.url;
  } catch (err) {
    console.error('Stripe checkout error:', err);
    showErrorToast(messageFromError(err, 'Could not open checkout. Please try again.'));
  }
}
